import { useEffect, useMemo, useState } from 'react'
import { Button, Drawer, Flex, Grid, Skeleton, Table, Tooltip, Typography } from 'antd'
import type { ColumnType } from 'antd/es/table'
import { PrinterOutlined } from '@ant-design/icons'
import { useTranslation } from 'react-i18next'
import { formatDateTime } from '@/shared/lib/format'
import { LoadError } from '@/shared/ui/LoadError'
import { VoucherAwaiting } from './VoucherAwaiting'
import { VoucherCodeText } from './VoucherCodeText'
import { VoucherStateTag } from './VoucherStateTag'
import { printableCards } from './voucherCode'
import { useBatchCodes } from './useVoucherStock'
import { isEndpointMissing, MY_CODES_ENDPOINT, type VoucherBatchRow, type VoucherCard } from './voucherStockApi'

/**
 * «Kim ishlatgan» ustuni — UCH HOLAT, ikkita emas.
 *
 * <h2>⛔ BO'SH QIYMATNING MA'NOSI HOLATGA BOG'LIQ</h2>
 * Bu ustunning butun qiymati shu ajratmada:
 *
 *   1. karta hali ishlatilmagan  → savolning O'ZI yo'q, bo'sh joy
 *      to'g'ri javob;
 *   2. karta ISHLATILGAN, lekin kim ishlatgani YOZILMAGAN → bu
 *      «hech kim» EMAS. Server bu ustunni hali umuman bermaydi
 *      (`voucherStockApi.ts` dagi o'lchov: `subscriber.voucher` da
 *      `activated_by` ustuni YO'Q va uning yo'qligi ongli qaror), ya'ni
 *      bugun HAR ishlatilgan karta shu holatda bo'ladi;
 *   3. nom bor → nom.
 *
 * 1 va 2 ni bitta chiziqcha bilan ko'rsatish eng qimmat xato bo'lardi:
 * sherik ishlatilgan kartani «hech kim tegmagan» deb o'qib, uni qaytadan
 * sotardi.
 */
function UsedBy({ card }: { card: VoucherCard }) {
  const { t } = useTranslation()

  if (card.state !== 'ACTIVATED') return <>{t('common.dash')}</>
  if (card.activatedByName !== null) return <>{card.activatedByName}</>

  return (
    <Tooltip title={t('myVouchers.usedBy.notRecordedHint')}>
      <Typography.Text type="secondary" style={{ fontSize: 12 }}>
        {t('myVouchers.usedBy.notRecorded')}
      </Typography.Text>
    </Tooltip>
  )
}

interface BatchCodesDrawerProps {
  /** `null` — oyna yopiq */
  batch: VoucherBatchRow | null
  /** Paketning foydalanuvchi tilidagi nomi */
  planName: string | null
  onClose: () => void
  /** Chop etish oynasini ochadi: tanlangan tartib raqamlari yoki `null` — butun partiya */
  onPrint: (serials: readonly number[] | null) => void
}

/**
 * Partiyadagi kartalar ro'yxati.
 *
 * <h2>⛔ KODLAR KESHGA TUSHMAYDI</h2>
 * `useBatchCodes` ataylab `useQuery` EMAS (o'sha hook izohi): kod bearer
 * sir va u oyna yopilganda xotiradan yo'qolishi kerak. Shuning uchun bu
 * yerda oyna yopilishida {@link useBatchCodes.reset} CHAQIRILADI —
 * usiz ro'yxat komponent qayta chizilguncha holatda qolardi.
 *
 * <h2>⚠️ CHOP ETISH IKKI YO'L BILAN, IKKALASI HAM SHU YERDAN</h2>
 * Egasining talabi: «bittalab kerak bo'lsa chop etadi». Ya'ni:
 *   · sarlavhadagi tugma — BUTUN partiya (yoki belgilangan kartalar);
 *   · har qatordagi tugma — AYNAN o'sha bitta karta.
 * Ikkalasi ham ayni maketga boradi (`VoucherPrintSheet`), farq faqat
 * yuborilgan ro'yxatda.
 */
export function BatchCodesDrawer({ batch, planName, onClose, onPrint }: BatchCodesDrawerProps) {
  const { t } = useTranslation()
  const screens = Grid.useBreakpoint()
  const isMobile = screens.md === false
  const codes = useBatchCodes()
  const [selected, setSelected] = useState<readonly string[]>([])

  const batchId = batch?.id ?? null
  const { load, reset } = codes

  useEffect(() => {
    setSelected([])
    if (batchId === null) {
      reset()
      return
    }
    load(batchId)
  }, [batchId, load, reset])

  const cards = codes.cards
  const byId = useMemo(() => new Map(cards.map((c) => [c.id, c])), [cards])
  // Belgilanmagan bo'lsa BUTUN partiya chop etiladi — «hammasi yoki
  // tanlangani» (egasi talabi). Filtr `printableCards` da, bu yerda emas.
  const toPrint = selected.length === 0 ? cards : selected.map((id) => byId.get(id)).filter(isCard)
  const printableCount = printableCards(toPrint).length

  const columns: ColumnType<VoucherCard>[] = [
    {
      title: t('myVouchers.col.serial'),
      render: (_v, card) => <Typography.Text type="secondary">{card.serialNo ?? t('common.dash')}</Typography.Text>,
      width: 64,
    },
    {
      title: t('myVouchers.col.code'),
      render: (_v, card) => (
        <VoucherCodeText code={card.code} state={card.state} prefix={batch?.codePrefix ?? null} />
      ),
    },
    {
      title: t('myVouchers.col.state'),
      render: (_v, card) => <VoucherStateTag state={card.state} />,
      width: 130,
    },
    {
      title: t('myVouchers.col.activatedAt'),
      // ⚠️ `activatedAt` FAQAT `ACTIVATED` da ma'noli. Boshqa holatda u
      // baribir bo'sh keladi va chiziqcha chiziladi.
      render: (_v, card) => (card.activatedAt ? formatDateTime(card.activatedAt) : t('common.dash')),
      width: 170,
    },
    {
      title: t('myVouchers.col.usedBy'),
      render: (_v, card) => <UsedBy card={card} />,
      ellipsis: true,
    },
    {
      title: t('common.actions'),
      width: 60,
      render: (_v, card) => (
        <Tooltip title={t('myVouchers.print.one')}>
          <Button
            type="text"
            size="small"
            aria-label={t('myVouchers.print.one')}
            icon={<PrinterOutlined />}
            // ⛔ Ishlatilgan kartada tugma O'CHIRILGAN: maket uni
            // baribir tashlab ketardi (`printableCards`) va bosilgan
            // tugma bo'sh oyna ochib, «buzuq» bo'lib ko'rinardi.
            disabled={printableCards([card]).length === 0 || card.serialNo === null}
            onClick={() => onPrint(serialsOf([card]))}
          />
        </Tooltip>
      ),
    },
  ]

  return (
    <Drawer
      open={batch !== null}
      onClose={onClose}
      width={isMobile ? '100%' : 720}
      destroyOnHidden
      title={
        batch === null
          ? t('myVouchers.codesTitle')
          : t('myVouchers.codesTitleNo', {
              no: batch.batchNo ?? t('myVouchers.batchNoMissing'),
            })
      }
      extra={
        <Button
          type="primary"
          icon={<PrinterOutlined />}
          disabled={printableCount === 0}
          onClick={() => onPrint(selected.length === 0 ? null : serialsOf(toPrint))}
        >
          {selected.length === 0
            ? t('myVouchers.print.all')
            : t('myVouchers.print.selected', { count: selected.length })}
        </Button>
      }
    >
      <Flex vertical gap={12}>
        {/* ⚠️ PAKET NOMI OYNADA TAKRORLANADI. Jadval bir necha o'nlab
            qatorli bo'lishi mumkin va sherik pastga tushib ketganda
            «bu qaysi partiya edi» degan savolga javob qolmasdi —
            sarlavha esa aylantirilganda ko'rinmay qoladi. */}
        {planName === null ? null : (
          <Typography.Text strong>{planName}</Typography.Text>
        )}

        {/* ⚠️ SIR EKANI EKRANDA AYTILADI. Nuqtalar o'zi tushuntirmaydi:
            sherik ularni «server bermadi» deb o'qishi mumkin edi. */}
        <Typography.Text type="secondary" style={{ fontSize: 12 }}>
          {t('myVouchers.code.secretNote')}
        </Typography.Text>

        {codes.loading ? <Skeleton active paragraph={{ rows: 5 }} /> : null}

        {codes.error && isEndpointMissing(codes.error) ? (
          <VoucherAwaiting endpoint={MY_CODES_ENDPOINT} note={t('myVouchers.awaitingCodes')} />
        ) : null}
        {codes.error && !isEndpointMissing(codes.error) ? (
          <LoadError titleKey="myVouchers.codesFailed" error={codes.error} />
        ) : null}

        {!codes.loading && !codes.error ? (
          <Table<VoucherCard>
            dataSource={[...cards]}
            rowKey={(card) => card.id}
            pagination={false}
            size="small"
            scroll={{ x: true }}
            rowSelection={{
              selectedRowKeys: [...selected],
              onChange: (keys) => setSelected(keys.map(String)),
            }}
            columns={columns}
          />
        ) : null}
      </Flex>
    </Drawer>
  )
}

function serialsOf(cards: readonly VoucherCard[]): number[] {
  return cards.flatMap((card) => (card.serialNo === null ? [] : [card.serialNo]))
}

/** `undefined` ni tur darajasida chiqarib tashlaydi (`Array.filter` uchun) */
function isCard(card: VoucherCard | undefined): card is VoucherCard {
  return card !== undefined
}
